import React, { Fragment, useContext, useRef } from 'react';

import infoSvg from '@assets/img/icons/info.svg';
import { PriceSimulatorContext } from '@components/utilities/PriceSimulator/helpers';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { getCityLabelByName } from '@data/cities';
import { moneyStrings } from '@data/money';
import { priceSimulatorStrings } from '@data/priceSimulator';
import { useIsAdmin } from '@hooks/useIsAdmin';
import type { BookingInquiryCargoForm } from '@models/next/BookingInquiryForms';
import { InvoiceHelper } from '@models/next/InvoiceHelper';
import type { RoutePlanSimulator } from '@models/next/RoutePlanSimulator';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { id } from '@utils/types';
import clsx from 'clsx';
import useHoverDirty from 'react-use/lib/useHoverDirty';
import * as R from 'remeda';

const strings = createStrings({
  ja: {
    hintClientThankYou: 'ご入力ありがとうございます。',
    hintSimulatorResult: `${priceSimulatorStrings.priceSimulator}の結果は以下の通りです`,
    expectedCost: '見積金額',
    tryAgain: 'もう一度料金をシミュレート',
    priceSettlementTitle: 'ご入力ありがとうございます。',
    priceSettlementSubtitle: '本件貨物の運送料金が出ました。',
    totalCost: '合計',
    bookingCreate: '今すぐ予約',
    joinMember: '今すぐD2D MEMBERSになる',
    baseKgHintString:
      'D2Dの最小重量は280KGSです。280KGS未満の数字が入力された場合には自動で最小重量が表示されます。',
    warehouse: '倉庫',
    port: '港',
    domesticReceiver: '日本国内納品先',
    mainRightTitle: 'ご入力条件',
    expectedCostHint:
      '*こちらの見積金額は、お客様にご入力いただいた貨物情報をもとに算出した概算見積金額です。物量の増減や配送方法のオプション選択などによって金額が変動する場合がございます。',
    noCargos: '集荷の荷物はありません',
    includedCost: (hasPickups: boolean) => (
      <>
        ■見積金額に含まれる料金
        <ul>
          {hasPickups && <li>中国集荷料金</li>}
          {hasPickups && (
            <li>中国国内料金（中国輸出通関料、中国AHR、中国CFSチャージ、中国倉庫入庫料）</li>
          )}
          <li>D2D料金（海上輸送料金、日本輸入通関料金、日本国内配送料金）</li>
        </ul>
      </>
    ),
    excludedCost: (hasPickups: boolean) => (
      <>
        ■含まれていない料金
        <ul>
          {!hasPickups && (
            <li>中国国内料金（中国輸出通関料、中国AHR、中国CFSチャージ、中国倉庫入庫料）</li>
          )}
          <li>日本の輸入関税、輸入消費税</li>
          <li>税関検査料金 ※発生時のみ</li>
          <li>外航貨物海上保険保険料 ※必要な場合のみ</li>
        </ul>
      </>
    ),
    reference: () => (
      <>
        ■備考
        <ul>
          <li>
            シミュレーターの料金には、離島など一部地域への追加送料は含まれておりません。別途計算が必要になる場合がございます。
          </li>
          <li>輸送中の道路状況、天候などによって、結果通りの輸送日数ではない場合もございます。</li>
          <li>
            極めて限定的ですが、D2Dがサービスを提供していない地域があります。詳細は当社へお問い合わせください。
          </li>
          <li>
            1個の貨物実重量は1000KG以上、もしくは1個の貨物、1辺サイズが2M以上がある場合、料金が変動する可能性があります。
          </li>
          <li>アマゾン/楽天への納品は事前の連絡をお願いします。</li>
          <li>
            料金設定や計算方法に変更が生じる場合がございますので、ご依頼までに期間が開く場合には再度料金シミュレーターでご確認ください。
          </li>
        </ul>
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  priceSettlementContent: {
    ...common.middleText,
    background: theme.palette.background.paper,
    padding: '40px 35px',
  },
  priceSettlementTitle: {
    ...common.textPrimary,
    ...common.fontWeight400,
    ...common.textAlignCenter,
    fontSize: 32,
  },
  priceSettlementMain: {
    ...common.textGray500,
    display: 'grid',
    gridTemplateColumns: '1fr 1px 1fr',
    padding: '30px 60px',
    columnGap: 40,
    background: theme.palette.grey[50],
  },
  priceSettlementMainSplit: {
    borderLeft: `1px dotted ${theme.palette.grey[500]}`,
  },
  priceSettlementLeftSplit: {
    borderBottom: `1px dotted ${theme.palette.grey[500]}`,
  },
  priceSettlementPriceDisplay: {
    ...common.middleText,
    fontSize: 15,
    marginTop: 20,
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    rowGap: 16,
    '& > div:nth-child(even)': {
      ...common.textAlignRight,
    },
  },
  baseKgHintContainer: {
    ...common.baseButton,
    display: 'inline',
    position: 'relative',
    marginLeft: '0.5rem',
    top: 1,
  },
  hintContainer: {
    ...common.exSmallerText,
    padding: 9,
    lineHeight: 1.75,
    backgroundColor: '#F2F7F8',
    position: 'absolute',
    left: 'calc(100% + 5px)',
    bottom: '50%',
    width: 250,
    zIndex: 100,
    border: `1px solid ${theme.palette.primary.main}`,
  },
  mainRightContainer: {
    ...common.flexColumn,
    rowGap: 10,
  },
  priceSettlementInputDisplay: {
    ...common.middleText,
    fontSize: 15,
    marginTop: 10,
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    rowGap: 16,
    '& > div:nth-child(even)': {
      ...common.textAlignRight,
    },
  },
  priceSettlementInputCargoDisplay: {
    ...common.middleText,
    display: 'grid',
    gridColumn: '1 / -1',
    gridTemplateColumns: '1fr auto',
    rowGap: 4,
    marginTop: -10,
    '& > div:nth-child(even)': {
      ...common.textAlignRight,
    },
  },
  explanationContainer: {
    ...common.middleText,
    ...common.flexColumn,
    ...common.textGray700,
    rowGap: 8,
    marginTop: 20,
    '& ul': {
      padding: 0,
      margin: 0,
      lineHeight: 1.75,
    },
    '& li': {
      listStyleType: 'none',
    },
  },
}));

const BaseKgHint: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const infoRef = useRef<HTMLImageElement>(null);

  const isHoveringInfo = useHoverDirty(infoRef);

  return (
    <div className={classes.baseKgHintContainer}>
      <img alt="Info" src={infoSvg} ref={infoRef} />
      {isHoveringInfo && <div className={classes.hintContainer}>{strings.baseKgHintString}</div>}
    </div>
  );
};

export const PriceSimulatorPriceSettlement: React.FC = () => {
  const isAdmin = useIsAdmin();
  const { routePlanSimulators, formOutput } = useContext(PriceSimulatorContext);

  if (!routePlanSimulators || !formOutput) throw new Error('The form is not complete.');

  const { has_pickups, receiver } = formOutput;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const renderSimulator = (routePlanSimulator: RoutePlanSimulator, i: number) => {
    const {
      simulator: { invoice_details },
    } = routePlanSimulator;

    // TODO: replace getInvoiceDetailsComputed
    const chinaPickupCharge = R.sumBy(invoice_details, (c) =>
      c.invoice_name === '中国集荷料金'
        ? InvoiceHelper.getInvoiceDetailsComputed(c).totalPriceForCurrency(
            c.exchange_base_rate,
            'JPY',
          ) ?? 0
        : 0,
    );

    const chinaLocalCharge = R.sumBy(invoice_details, (c) =>
      ['中国倉庫入庫料', '中国AHRチャージ', '中国輸出通関料', '中国CFSチャージ'].includes(
        c.invoice_name,
      )
        ? InvoiceHelper.getInvoiceDetailsComputed(c).totalPriceForCurrency(
            c.exchange_base_rate,
            'JPY',
          ) ?? 0
        : 0,
    );

    const d2dTargetTaxPrice = R.sumBy(invoice_details, (c) =>
      c.invoice_name === 'D2D料金（課税分）'
        ? InvoiceHelper.getInvoiceDetailsComputed(c).totalPriceForCurrency(
            c.exchange_base_rate,
            'JPY',
          ) ?? 0
        : 0,
    );

    const d2dNonTargetTaxPrice = R.sumBy(invoice_details, (c) =>
      c.invoice_name === 'D2D料金（非課税分）'
        ? InvoiceHelper.getInvoiceDetailsComputed(c).totalPriceForCurrency(
            c.exchange_base_rate,
            'JPY',
          ) ?? 0
        : 0,
    );

    const totalCost = R.sumBy(
      [
        has_pickups ? chinaPickupCharge : 0,
        has_pickups ? chinaLocalCharge : 0,
        d2dTargetTaxPrice,
        d2dNonTargetTaxPrice,
      ],
      id,
    );

    return (
      <section key={i}>
        <div
          className={clsx(
            classes.textPrimary,
            classes.normalText,
            classes.fontWeight600,
            classes.textAlignCenter,
          )}
          style={{ marginBottom: 20 }}
        >
          {has_pickups
            ? bookingInquiryStrings.pickupAddress
            : `${getCityLabelByName(routePlanSimulator.route_plan.receiving_city)}${
                strings.warehouse
              }`}

          {'→'}
          {getCityLabelByName(routePlanSimulator.route_plan.pol)}
          {strings.port}
          {'→'}
          {getCityLabelByName(routePlanSimulator.route_plan.pod)}
          {strings.port}
        </div>

        <div
          className={clsx(classes.textPrimary, classes.normalText, classes.textAlignCenter)}
          style={{ marginBottom: 20 }}
        >
          {strings.domesticReceiver}
          {': '}
          {receiver.pref}
          {receiver.city}
        </div>

        <div
          className={clsx(
            classes.backgroundPrimaryAlpha5,
            classes.biggerText,
            classes.flexInline,
            classes.justifyBetween,
            classes.textPrimary,
          )}
          style={{ padding: 7, marginBottom: 20 }}
        >
          <div>{strings.totalCost}</div>
          <div>{moneyStrings.formatYen(totalCost)}</div>
        </div>

        <div className={classes.priceSettlementLeftSplit} />

        <div className={classes.priceSettlementPriceDisplay}>
          <div>{priceSimulatorStrings.total_lead_time}</div>
          <div role="data">{routePlanSimulator.simulator.delivery_days}日</div>

          <div>
            {priceSimulatorStrings.base_kg}
            <BaseKgHint />
          </div>
          <div role="data">{routePlanSimulator.simulator.target_kg} KGS</div>

          {has_pickups && (
            <>
              <div>中国集荷料金</div>
              <div role="data">{moneyStrings.formatYen(chinaPickupCharge)}</div>

              <div>{priceSimulatorStrings.chinaCharges}</div>
              <div role="data">{moneyStrings.formatYen(chinaLocalCharge)}</div>
            </>
          )}

          <div>D2D料金（課税分）</div>
          <div role="data">{moneyStrings.formatYen(d2dTargetTaxPrice)}</div>

          <div>D2D料金（非課税分）</div>
          <div role="data">{moneyStrings.formatYen(d2dNonTargetTaxPrice)}</div>
        </div>
      </section>
    );
  };

  const renderMainLeft = () => {
    return (
      <div className={classes.flexColumn} style={{ rowGap: 60 }}>
        {routePlanSimulators.map(renderSimulator)}
      </div>
    );
  };

  const renderMainRight = () => {
    const renderCargo = (v: BookingInquiryCargoForm, i: number) => {
      return (
        <Fragment key={i}>
          <div>{bookingInquiryStrings.cargoName}</div>
          <div>{v.name}</div>

          <div className={classes.priceSettlementInputCargoDisplay}>
            <div>{bookingInquiryStrings.cargoNum}</div>
            <div>{v.num}</div>

            {v.tag === 'WV' && (
              <>
                <div>{bookingInquiryStrings.totalWeight}</div>
                <div>{v.total_weight}</div>

                <div>{bookingInquiryStrings.totalVolume}</div>
                <div>{v.total_volume}</div>
              </>
            )}

            {v.tag === 'LWH' && (
              <>
                <div>{bookingInquiryStrings.cargoSize}</div>
                <div>
                  {v.length}:{v.width}:{v.height}
                </div>

                <div>{bookingInquiryStrings.weight}</div>
                <div>{v.weight}</div>
              </>
            )}
          </div>

          {/* To ensure that odd divs are label and even divs are values, we add a dummy cell here */}
          <div style={{ display: 'none' }} />
        </Fragment>
      );
    };

    const renderHasPickupsCargos = () => {
      return (
        <>
          {formOutput.pickup_addresses.map((pickup_address, i) => (
            <Fragment key={i}>
              <div className={classes.fontWeight600}>
                {bookingInquiryStrings.pickupAddress} {i + 1}
              </div>
              <div className={classes.fontWeight600}>{pickup_address.city_name}</div>

              {R.pipe(
                formOutput.cargos
                  .filter((cargo) => cargo.pickup_address_index - 1 === i)
                  .map(renderCargo),
                (c) =>
                  c.length ? (
                    c
                  ) : (
                    <>
                      <div style={{ gridColumn: '1 / -1', opacity: 0.5 }}>{strings.noCargos}</div>
                      {/* To ensure that odd divs are label and even divs are values, we add a dummy cell here */}
                      <div style={{ display: 'none' }} />
                    </>
                  ),
              )}
            </Fragment>
          ))}
        </>
      );
    };

    const renderNoPickupsCargos = () => {
      return (
        <>
          <div>{bookingInquiryStrings.receivingWarehouse}</div>
          <div>{getCityLabelByName(formOutput.cargos_port_name) + strings.warehouse}</div>

          {formOutput.cargos.map(renderCargo)}
        </>
      );
    };

    return (
      <div className={classes.mainRightContainer}>
        <div className={clsx(classes.fontWeight600, classes.normalText)}>
          {strings.mainRightTitle}
        </div>

        <div className={classes.priceSettlementInputDisplay}>
          <div>{bookingInquiryStrings.collectOption}</div>
          <div>{formOutput.has_pickups ? formStrings.use : formStrings.notUse}</div>

          {formOutput.has_pickups ? renderHasPickupsCargos() : renderNoPickupsCargos()}

          <div>{strings.domesticReceiver}</div>
          <div>
            {receiver.pref}
            {receiver.city}
          </div>

          <div>{bookingInquiryStrings.deliveryOption}</div>
          <div>{bookingInquiryStrings.truckTypes[formOutput.truck_type]}</div>
        </div>
      </div>
    );
  };

  return (
    <div className={classes.priceSettlementContent}>
      <div className={classes.priceSettlementTitle}>
        {priceSimulatorStrings.priceSimulatorResult}
      </div>
      <div
        className={clsx(classes.textAlignCenter, classes.textGray500)}
        style={{ marginBottom: 20 }}
      >
        {!isAdmin && strings.hintClientThankYou}
        <br />
        {strings.hintSimulatorResult}
        <br />
      </div>

      <main className={classes.priceSettlementMain}>
        {renderMainLeft()}
        <div className={classes.priceSettlementMainSplit} />
        {renderMainRight()}
      </main>

      <section className={classes.explanationContainer}>
        <div className={classes.textError}>{strings.expectedCostHint}</div>

        <div>{strings.includedCost(has_pickups)}</div>

        <div>{strings.excludedCost(has_pickups)}</div>

        <div>{strings.reference()}</div>
      </section>
    </div>
  );
};
